import { memo, useState, useEffect } from "react";
import { Flex, Box, Text, ThemeUIStyleObject } from "theme-ui";
import { CircularProgressbarWithChildren } from "react-circular-progressbar";
import { buildStyles } from "react-circular-progressbar";
import "react-circular-progressbar/dist/styles.css";
import { Icon } from "./Icon";
import type { TransactionState } from "./Transaction";
import { usePSYModal } from "src/hooks/usePSYModal"
import { ReactModal } from "src/components/ReactModal"
import { Loader } from './PSY/Loader'

const strokeWidth = 10;

const circularProgressbarStyle = {
  strokeLinecap: "butt",
  pathColor: "white",
  trailColor: "rgba(255, 255, 255, 0.33)"
};

const slowProgress = {
  strokeWidth,
  styles: buildStyles({
    ...circularProgressbarStyle,
    pathTransitionDuration: 30
  })
};

const fastProgress = {
  strokeWidth,
  styles: buildStyles({
    ...circularProgressbarStyle,
    pathTransitionDuration: 0.75
  })
};

export type TransactionStateType = TransactionState["type"];

const Donut = memo(
  CircularProgressbarWithChildren,
  ({ value: prev }, { value: next }) => prev === next
);

type TransactionProgressDonutProps = {
  state: TransactionStateType;
};

const TransactionProgressDonut: React.FC<TransactionProgressDonutProps> = ({ state }) => {
  const [value, setValue] = useState(0);
  const maxValue = 1;

  useEffect(() => {
    if (state === "confirmed") {
      setTimeout(() => setValue(maxValue), 40);
    } else {
      setTimeout(() => setValue(maxValue * 0.67), 20);
    }
  }, [state]);

  return state === "confirmed" ? (
    <Icon name="check" color="white" size="lg" />
  ) : state === "failed" || state === "cancelled" ? (
    <Icon name="times" color="white" size="lg" />
  ) : (
    <Loader />
  );
};

type TransactionStatusProps = {
  state: TransactionStateType;
  message?: string;
  style?: ThemeUIStyleObject;
};

export const TransactionStatus: React.FC<TransactionStatusProps> = ({ state, message, style }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  useEffect(() => {
     const isWaiting = !(state === "idle" || state === "waitingForApproval")
      setIsModalOpen(isWaiting)
  }, [])

  return (
    <ReactModal
      isOpen={isModalOpen}
      onDismiss={() => {
        setIsModalOpen(false);
      }}
    >
      <div
        className="flex flex-row items-center justify-center bg-card py-8 px-6"
      >
        <TransactionProgressDonut state={state} />
        <div className="typo-heading-04 text-white ml-4">
          {state === "waitingForConfirmation"
            ? "Waiting for confirmation"
            : state === "cancelled"
            ? "Cancelled"
            : state === "failed"
            ? message || "Transaction failed. Please try again."
            : "Confirmed"}
        </div>
      </div>
    </ReactModal>
  );
};
